import cn from 'classnames';
import { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { SharingInvitation } from '@/shared/api/protocol-ts/model/dto_sharing_pb';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import {
  Avatar,
  Button,
  Icon,
  Skeleton,
  Tooltip,
  WidgetCard,
} from '@/shared/ui';

import { accessModel } from '@/entities/access';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';

import { SharedDoctorRow } from '@/features/sharedDoctorRow';
import {
  RevokeSharedPatientModal,
  SharePatientModalForm,
} from '@/features/sharePatient';
import { ShowAllSharedWithModal } from '@/features/showAllSharedWithList';

import { MAX_ROWS_COUNT } from '../../config';
import { getShortedList } from '../../lib';

import styles from './SharedWith.module.scss';

type SharedWithProps = {
  className?: string;
  patientID: string;
};

export const SharedWith: FC<SharedWithProps> = (props) => {
  const { className, patientID } = props;
  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();

  const acceptedOrPendingSharedPatientInvitations = useAppSelector(
    accessModel.sharingSelectors
      .selectAcceptedOrPendingSharedPatientInvitations,
  );

  const loading = useAppSelector(
    accessModel.sharingSelectors.selectSharingLoading,
  );

  const { CanCancelOthersSharings, CanViewSharings, CanShare } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );

  const isForbidSharing = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_Sharing,
    ),
  );

  const openShowAllModal = useCallback(() => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.AllSharedWith,
        data: { patientID },
      }),
    );
  }, [patientID]);

  const openSharePatientFormModal = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      event.stopPropagation();
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.SharePatient,
          data: { patientID },
        }),
      );
    },
    [dispatch, patientID],
  );

  const onCardClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (isMobile) {
        openShowAllModal();
      }
    },
    [isMobile, openShowAllModal],
  );

  const shortedSharedWithList = getShortedList(
    acceptedOrPendingSharedPatientInvitations,
  ) as SharingInvitation[];

  if (loading === 'pending') {
    return (
      <Skeleton
        className={cn(styles.container, className)}
        borderRadius={16}
        height="100%"
        width="100%"
      />
    );
  }

  const areInvitations = !!acceptedOrPendingSharedPatientInvitations.length;
  const showDesktopInvitations = areInvitations && !isMobile;
  const showMobileInvitaions = areInvitations && isMobile;
  const shouldShowAllAppear =
    acceptedOrPendingSharedPatientInvitations.length > MAX_ROWS_COUNT;

  // Hide the entire widget if sharing is forbidden or user can't view sharings
  if (isForbidSharing || !CanViewSharings) {
    return null;
  }

  return (
    <>
      <WidgetCard
        className={cn(styles.container, className)}
        data-section="shared-with"
        onClick={onCardClick}
      >
        <div className={styles.header}>
          <h4 className={cn(styles.title, isMobile ? 'h4m' : 'h4')}>
            <FormattedMessage
              id="patientInfo.sharedWith"
              defaultMessage="Shared with"
            />
          </h4>

          {CanShare && (
            <Tooltip.Primary
              content={
                <FormattedMessage
                  id="patientInfo.sharePatient"
                  defaultMessage="Share"
                />
              }
            >
              <Icon
                name="boldShare"
                className={styles.icon}
                onClick={openSharePatientFormModal}
                size={isMobile ? 24 : 32}
              />
            </Tooltip.Primary>
          )}
        </div>

        {showDesktopInvitations && (
          <div className={styles.sharedList}>
            {shortedSharedWithList.map((sharedPatientInvitation) => (
              <SharedDoctorRow
                key={sharedPatientInvitation?.ID}
                sharedPatientInvitation={sharedPatientInvitation}
                canCancelSharing={CanCancelOthersSharings}
                openShowAllModal={openShowAllModal}
                lessInfo
              />
            ))}
          </div>
        )}

        {showMobileInvitaions && (
          <Avatar.Group>
            {shortedSharedWithList.map((sharedPatientInvitation) => (
              <Avatar
                key={sharedPatientInvitation?.ID}
                initials={sharedPatientInvitation.Recipient?.Email?.at(
                  0,
                )?.toUpperCase()}
                size={32}
              />
            ))}
          </Avatar.Group>
        )}

        {!areInvitations && (
          <div className={styles.emptyList}>
            <Icon
              className={styles.emptyListIcon}
              name="users"
              size={isMobile ? 32 : 64}
            />
          </div>
        )}

        {shouldShowAllAppear && (
          <Button variant="tertiary" size="medium" onClick={openShowAllModal}>
            <FormattedMessage
              id="patientInfo.showAll"
              defaultMessage="Show all"
            />
          </Button>
        )}
      </WidgetCard>

      <SharePatientModalForm />
      <RevokeSharedPatientModal />
      <ShowAllSharedWithModal />
    </>
  );
};
